import React, { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Box, Card, Grid, Stack, Typography, Chip, Button, Divider,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  Select, MenuItem, FormControl, InputLabel, useTheme, useMediaQuery
} from "@mui/material";
import { DataGrid, type GridColDef, type GridRenderCellParams } from "@mui/x-data-grid";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PrintIcon from "@mui/icons-material/Print";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import AddIcon from "@mui/icons-material/AddBox";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import PaymentsIcon from "@mui/icons-material/Payments";
import { PageHeader } from "../components/PageHeader.js";
import { DialogHelp } from "../components/DialogHelp.js";
import { ConfirmDialog } from "../components/ConfirmDialog.js";
import { SettledChip } from "../components/StatusChip.js";
import { RowQuickActions } from "../components/RowQuickActions.js";
import { CreditEditDialog } from "../components/CreditEditDialog.js";
import { CreditCreateDialog } from "../components/CreditCreateDialog.js";
import { useApi } from "../hooks/useApi.js";
import { useReceiptWhatsApp } from "../hooks/useReceiptWhatsApp.js";
import { formLabel } from "../lib/labels.js";
import { PAYMENT_TYPES } from "../lib/payments.js";
import type { Receipt, Partner, Credit, Debit } from "@kappa-v2/contracts";
import { format } from "date-fns";

const eur = (n?: number) =>
  `€ ${(n ?? 0).toLocaleString("it-IT", { minimumFractionDigits: 2 })}`;

const fmtDate = (d?: Date | string | null) =>
  d ? format(new Date(d), "dd/MM/yyyy") : "—";

const todayISO = () => new Date().toISOString().slice(0, 10);

interface PaymentForm {
  _id?: string;
  debit: number;
  datePaid: string;
  paymentType: string;
}

export function ReceiptDetailPage() {
  const { t } = useTranslation();
  const { id, partnerId, period } = useParams();
  const navigate = useNavigate();
  const { call, openPdf, printPdf } = useApi();
  const sendReceiptWa = useReceiptWhatsApp();
  const [waOpen, setWaOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [receipt, setReceipt] = useState<Receipt | null>(null);
  const [partner, setPartner] = useState<Partner | null>(null);
  const [credits, setCredits] = useState<Credit[]>([]);
  const [debits, setDebits] = useState<Debit[]>([]);
  const [loading, setLoading] = useState(true);

  const [payOpen, setPayOpen] = useState(false);
  const [payForm, setPayForm] = useState<PaymentForm>({ debit: 0, datePaid: todayISO(), paymentType: "form.payment.cash" });
  const [saving, setSaving] = useState(false);
  const [deleteDebit, setDeleteDebit] = useState<Debit | null>(null);
  const [deleteCredit, setDeleteCredit] = useState<Credit | null>(null);
  const [editCredit, setEditCredit] = useState<Credit | null>(null);
  const [addCreditOpen, setAddCreditOpen] = useState(false);

  const unwrap = <T,>(v: { data: T[] } | T[]): T[] => (Array.isArray(v) ? v : (v.data ?? []));

  const fetchAll = useCallback(() => {
    if (!id || !partnerId || !period) return;
    setLoading(true);
    Promise.allSettled([
      call<Receipt>(`/api/v1/payments/receipts/${id}`),
      call<Partner>(`/api/v1/partners/${partnerId}`),
      call<{ data: Credit[] } | Credit[]>(`/api/v1/payments/credits?personId=${partnerId}&period=${encodeURIComponent(period)}`),
      call<{ data: Debit[] } | Debit[]>(`/api/v1/payments/debits?personId=${partnerId}&period=${encodeURIComponent(period)}`)
    ]).then(([rRes, pRes, cRes, dRes]) => {
      if (rRes.status === "fulfilled") setReceipt(rRes.value);
      if (pRes.status === "fulfilled") setPartner(pRes.value);
      if (cRes.status === "fulfilled") setCredits(unwrap(cRes.value));
      if (dRes.status === "fulfilled") setDebits(unwrap(dRes.value));
    }).finally(() => setLoading(false));
  }, [call, id, partnerId, period]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const remaining = (receipt?.totalCredit ?? 0) - (receipt?.totalDebit ?? 0);
  const pdfPath = `/api/v1/payments/receipts/${id}/pdf`;

  const openNewPayment = () => {
    setPayForm({ debit: remaining > 0 ? remaining : 0, datePaid: todayISO(), paymentType: "form.payment.cash" });
    setPayOpen(true);
  };
  const openEditPayment = (d: Debit) => {
    setPayForm({
      _id: d._id,
      debit: d.debit,
      datePaid: format(new Date(d.datePaid), "yyyy-MM-dd"),
      paymentType: d.paymentType ?? "form.payment.cash"
    });
    setPayOpen(true);
  };

  const savePayment = async () => {
    if (!partnerId || !period) return;
    setSaving(true);
    try {
      const body: Record<string, unknown> = {
        personId: partnerId,
        period,
        debit: Number(payForm.debit),
        datePaid: payForm.datePaid,
        paymentType: payForm.paymentType,
        partnerType: partner?.partnerType ?? "form.partnerType.other"
      };
      if (payForm._id) {
        // In modifica NON si tocca il numero ricevuta (niente "NEW", lo si lascia invariato).
        await call(`/api/v1/payments/debits/${payForm._id}`, { method: "PATCH", body });
      } else {
        await call("/api/v1/payments/debits", { method: "POST", body: { ...body, paymentNumber: "NEW" } });
      }
      setPayOpen(false);
      fetchAll();
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteDebit = async () => {
    if (!deleteDebit?._id) return;
    await call(`/api/v1/payments/debits/${deleteDebit._id}`, { method: "DELETE" });
    setDeleteDebit(null);
    fetchAll();
  };
  const handleDeleteCredit = async () => {
    if (!deleteCredit?._id) return;
    await call(`/api/v1/payments/credits/${deleteCredit._id}`, { method: "DELETE" });
    setDeleteCredit(null);
    fetchAll();
  };

  const buildDebitActions = (d: Debit) => [
    { key: "edit", label: t("receiptDetail.editAction"), description: t("receiptDetail.editActionDesc"), icon: <EditIcon fontSize="small" />, onClick: () => openEditPayment(d) },
    { key: "payments", label: t("receiptDetail.managePayments"), description: t("receiptDetail.managePaymentsDesc"), icon: <PaymentsIcon fontSize="small" />, onClick: () => navigate(`/protected/debits?personId=${partnerId}&period=${encodeURIComponent(period ?? "")}`) },
    { key: "del", label: t("receiptDetail.deleteAction"), description: t("receiptDetail.deletePaymentDesc"), icon: <DeleteIcon fontSize="small" />, destructive: true, dividerBefore: true, onClick: () => setDeleteDebit(d) }
  ];

  const buildCreditActions = (c: Credit) => [
    { key: "edit", label: t("common.edit"), description: t("credits.editTitle"), icon: <EditIcon fontSize="small" />, onClick: () => setEditCredit(c) },
    { key: "services", label: t("receiptDetail.manageCompetences"), description: t("receiptDetail.manageCompetencesDesc"), icon: <ShoppingCartIcon fontSize="small" />, onClick: () => navigate(`/protected/credits?personId=${partnerId}`) },
    { key: "del", label: t("receiptDetail.deleteAction"), description: t("receiptDetail.deleteCompetence"), icon: <DeleteIcon fontSize="small" />, destructive: true, dividerBefore: true, onClick: () => setDeleteCredit(c) }
  ];

  const debitColumns: GridColDef[] = [
    {
      field: "datePaid", headerName: t("common.date"), width: 120,
      renderCell: (p: GridRenderCellParams<Debit>) => fmtDate(p.row.datePaid)
    },
    {
      field: "paymentType", headerName: t("receiptDetail.method"), flex: 1, minWidth: 160,
      renderCell: (p: GridRenderCellParams<Debit>) => formLabel(p.row.paymentType) || "—"
    },
    {
      field: "paymentNumber", headerName: t("receiptDetail.receiptNumber"), width: 130,
      renderCell: (p: GridRenderCellParams<Debit>) => (
        <Typography variant="caption" color="text.secondary">{p.row.paymentNumber ?? "—"}</Typography>
      )
    },
    {
      field: "debit", headerName: t("common.amount"), width: 130, align: "right", headerAlign: "right",
      renderCell: (p: GridRenderCellParams<Debit>) => (
        <Typography variant="body2" fontWeight={700} color="success.dark">{eur(p.row.debit)}</Typography>
      )
    },
    {
      field: "actions", headerName: "", width: 90, sortable: false, align: "right", headerAlign: "center",
      renderCell: (p: GridRenderCellParams<Debit>) => (
        <RowQuickActions ariaLabel={t("common.actions")} quickKeys={["edit"]} items={buildDebitActions(p.row)} />
      )
    }
  ];

  const creditColumns: GridColDef[] = [
    {
      field: "name", headerName: t("common.name"), flex: 1, minWidth: 160,
      renderCell: (p: GridRenderCellParams<Credit>) => (
        <Typography variant="body2" fontWeight={600}>{p.row.name}</Typography>
      )
    },
    { field: "description", headerName: t("common.description"), flex: 1.4, minWidth: 200 },
    {
      field: "fromPartnerService", headerName: t("receiptDetail.fromSubscription"), width: 140, align: "center", headerAlign: "center",
      renderCell: (p: GridRenderCellParams<Credit>) => (
        <Chip size="small" variant="outlined" label={p.row.fromPartnerService ? t("common.yes") : t("common.no")}
          color={p.row.fromPartnerService ? "primary" : "default"} sx={{ fontSize: "0.72rem" }} />
      )
    },
    {
      field: "credit", headerName: t("common.amount"), width: 130, align: "right", headerAlign: "right",
      renderCell: (p: GridRenderCellParams<Credit>) => (
        <Typography variant="body2" fontWeight={700}>{eur(p.row.credit)}</Typography>
      )
    },
    {
      field: "actions", headerName: "", width: 90, sortable: false, align: "right", headerAlign: "center",
      renderCell: (p: GridRenderCellParams<Credit>) => (
        <RowQuickActions ariaLabel={t("common.actions")} quickKeys={["edit"]} items={buildCreditActions(p.row)} />
      )
    }
  ];

  return (
    <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
      <PageHeader
        title={receipt ? t("receiptDetail.titleWithNumber", { number: receipt.receiptsNumber }) : t("receiptDetail.title")}
        subtitle={partner
          ? t("receiptDetail.subtitle", { name: `${partner.firstName} ${partner.lastName}`, period })
          : t("receiptDetail.subtitlePeriod", { period })}
        actions={
          <>
            <Button data-tour="rd-actions" variant="outlined" size="small" startIcon={<ArrowBackIcon />} onClick={() => navigate("/protected/receipts")}>
              {t("common.back")}
            </Button>
            <Button variant="outlined" size="small" startIcon={<PrintIcon />} onClick={() => printPdf(pdfPath)} disabled={!receipt}>
              {t("common.print")}
            </Button>
            <Button variant="outlined" size="small" startIcon={<PictureAsPdfIcon />} onClick={() => openPdf(pdfPath)} disabled={!receipt}>
              {t("common.exportPdf")}
            </Button>
            <Button variant="contained" color="success" size="small" startIcon={<WhatsAppIcon />} onClick={() => setWaOpen(true)} disabled={!receipt}>
              {t("receipts.actions.whatsapp")}
            </Button>
          </>
        }
      />

      <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 }, flex: 1 }}>
        {/* Riepilogo scheda */}
        <Card sx={{ p: 2, mb: 2 }} data-tour="rd-summary">
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={6} sm={3}>
              <Typography variant="caption" color="text.secondary">{t("common.period")}</Typography>
              <Typography variant="h6">{period}</Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="caption" color="text.secondary">{t("receiptDetail.due")}</Typography>
              <Typography variant="h6">{eur(receipt?.totalCredit)}</Typography>
            </Grid>
            <Grid item xs={6} sm={2}>
              <Typography variant="caption" color="text.secondary">{t("receiptDetail.paid")}</Typography>
              <Typography variant="h6" color="success.dark">{eur(receipt?.totalDebit)}</Typography>
            </Grid>
            <Grid item xs={6} sm={2}>
              <Typography variant="caption" color="text.secondary">{t("receiptDetail.toPay")}</Typography>
              <Typography variant="h6" color={remaining > 0 ? "warning.main" : "text.primary"}>{eur(remaining)}</Typography>
            </Grid>
            <Grid item xs={6} sm={2}>
              <Typography variant="caption" color="text.secondary" display="block">{t("common.status")}</Typography>
              <SettledChip settled={receipt?.settled ?? false} />
            </Grid>
          </Grid>
        </Card>

        {/* Anagrafica socio */}
        <Card sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>{t("receiptDetail.headerLabel")}</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}><Typography variant="body2"><b>{partner?.firstName} {partner?.lastName}</b></Typography></Grid>
            <Grid item xs={12} sm={4}><Typography variant="body2" color="text.secondary">{partner?.fiscalCode ?? "—"}</Typography></Grid>
            <Grid item xs={12} sm={4}>
              <Typography variant="body2" color="text.secondary">
                {partner?.contacts?.[0]?.telephoneNumber ?? ""} {partner?.emails?.[0]?.email ? `· ${partner.emails[0].email}` : ""}
              </Typography>
            </Grid>
          </Grid>
        </Card>

        {/* Pagamenti */}
        <Card sx={{ mb: 2 }} data-tour="rd-payments">
          <Box sx={{
            p: 2, borderBottom: "1px solid rgba(0,0,0,.06)",
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "stretch", sm: "center" },
            gap: 1.5
          }}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ flex: 1 }}>
              {t("receiptDetail.paymentsTitle", { amount: eur(receipt?.totalDebit) })}
            </Typography>
            <Button data-tour="rd-pay-add" variant="contained" size="small" startIcon={<AddIcon />} onClick={openNewPayment}>
              {t("receiptDetail.registerPayment")}
            </Button>
          </Box>
          {isMobile ? (
            debits.length === 0
              ? <Box sx={{ p: 3, textAlign: "center" }}><Typography variant="body2" color="text.secondary">{t("receiptDetail.noPayments")}</Typography></Box>
              : (
                <Box>
                  {debits.map((d, idx) => (
                    <Box key={d._id ?? idx} sx={{ p: 2, borderTop: idx ? "1px solid rgba(0,0,0,.06)" : "none", display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1 }}>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" fontWeight={700} color="success.dark">{eur(d.debit)}</Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          {fmtDate(d.datePaid)} · {formLabel(d.paymentType) || "—"}
                        </Typography>
                        {d.paymentNumber && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            {t("receiptDetail.receiptNumber")}: {d.paymentNumber}
                          </Typography>
                        )}
                      </Box>
                      <RowQuickActions ariaLabel={t("common.actions")} quickKeys={["edit"]} items={buildDebitActions(d)} />
                    </Box>
                  ))}
                </Box>
              )
          ) : (
            <DataGrid
              rows={debits} columns={debitColumns} getRowId={(r) => r._id ?? Math.random().toString()}
              loading={loading} autoHeight rowHeight={52} pageSizeOptions={[10, 25, 50]}
              initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
              disableRowSelectionOnClick sx={{
                border: "none",
                "& .MuiDataGrid-cell": { display: "flex", alignItems: "center" },
                "& .MuiDataGrid-cell--textRight": { justifyContent: "flex-end" },
                "& .MuiDataGrid-cell--textLeft": { justifyContent: "flex-start" },
                "& .MuiDataGrid-cell--textCenter": { justifyContent: "center" }
              }}
              localeText={{ noRowsLabel: t("receiptDetail.noPayments") }}
            />
          )}
        </Card>

        {/* Crediti / competenze */}
        <Card data-tour="rd-competences">
          <Box sx={{
            p: 2, borderBottom: "1px solid rgba(0,0,0,.06)", display: "flex",
            flexDirection: { xs: "column", sm: "row" }, alignItems: { xs: "stretch", sm: "center" }, gap: 1.5
          }}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ flex: 1 }}>
              {t("receiptDetail.competencesTitle", { amount: eur(receipt?.totalCredit) })}
            </Typography>
            <Button variant="contained" color="secondary" size="small" startIcon={<AddIcon />} onClick={() => setAddCreditOpen(true)} disabled={!partnerId || !period}>
              {t("receiptDetail.addCompetence")}
            </Button>
          </Box>
          {isMobile ? (
            credits.length === 0
              ? <Box sx={{ p: 3, textAlign: "center" }}><Typography variant="body2" color="text.secondary">{t("receiptDetail.noCompetences")}</Typography></Box>
              : (
                <Box>
                  {credits.map((c, idx) => (
                    <Box key={c._id ?? idx} sx={{ p: 2, borderTop: idx ? "1px solid rgba(0,0,0,.06)" : "none" }}>
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1 }}>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2" fontWeight={700}>{c.name}</Typography>
                          <Typography variant="caption" color="text.secondary" display="block">{c.description}</Typography>
                        </Box>
                        <RowQuickActions ariaLabel={t("common.actions")} quickKeys={["edit"]} items={buildCreditActions(c)} />
                      </Box>
                      <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: "wrap", alignItems: "center" }}>
                        <Typography variant="body2" fontWeight={700}>{eur(c.credit)}</Typography>
                        <Chip size="small" variant="outlined"
                          label={`${t("receiptDetail.fromSubscription")}: ${c.fromPartnerService ? t("common.yes") : t("common.no")}`}
                          color={c.fromPartnerService ? "primary" : "default"} sx={{ fontSize: "0.72rem" }} />
                      </Stack>
                    </Box>
                  ))}
                </Box>
              )
          ) : (
            <DataGrid
              rows={credits} columns={creditColumns} getRowId={(r) => r._id ?? Math.random().toString()}
              loading={loading} autoHeight rowHeight={52} pageSizeOptions={[10, 25, 50]}
              initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
              disableRowSelectionOnClick sx={{
                border: "none",
                "& .MuiDataGrid-cell": { display: "flex", alignItems: "center" },
                "& .MuiDataGrid-cell--textRight": { justifyContent: "flex-end" },
                "& .MuiDataGrid-cell--textLeft": { justifyContent: "flex-start" },
                "& .MuiDataGrid-cell--textCenter": { justifyContent: "center" }
              }}
              localeText={{ noRowsLabel: t("receiptDetail.noCompetences") }}
            />
          )}
        </Card>
      </Box>

      {/* Dialog registra/modifica pagamento */}
      <Dialog open={payOpen} onClose={() => setPayOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: "flex", alignItems: "center" }}>
          {payForm._id ? t("receiptDetail.editPayment") : t("receiptDetail.registerPayment")}
          <DialogHelp steps={[{ title: t("help.formTitle"), description: t("help.forms.receiptPayment") }]} />
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={6}>
              <TextField fullWidth size="small" label={`${t("common.amount")} (€)`} type="number"
                value={payForm.debit}
                onChange={(e) => setPayForm((f) => ({ ...f, debit: Number(e.target.value) }))}
                inputProps={{ step: 0.01, min: 0 }} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" label={t("receiptDetail.paymentDate")} type="date"
                InputLabelProps={{ shrink: true }}
                value={payForm.datePaid}
                onChange={(e) => setPayForm((f) => ({ ...f, datePaid: e.target.value }))} />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel>{t("receiptDetail.paymentMethod")}</InputLabel>
                <Select label={t("receiptDetail.paymentMethod")} value={payForm.paymentType}
                  onChange={(e) => setPayForm((f) => ({ ...f, paymentType: e.target.value }))}>
                  {PAYMENT_TYPES.map((m) => <MenuItem key={m} value={m}>{formLabel(m)}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
          <Divider sx={{ mt: 2 }} />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
            {t("receiptDetail.summary", { due: eur(receipt?.totalCredit), paid: eur(receipt?.totalDebit), remaining: eur(remaining) })}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setPayOpen(false)}>{t("common.cancel")}</Button>
          <Button variant="contained" disabled={saving} onClick={savePayment}>
            {saving ? t("common.saving") : t("common.save")}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={Boolean(deleteDebit)}
        message={t("receiptDetail.deletePaymentConfirm", { amount: eur(deleteDebit?.debit) })}
        onConfirm={handleDeleteDebit}
        onCancel={() => setDeleteDebit(null)}
      />
      <ConfirmDialog
        open={Boolean(deleteCredit)}
        message={t("receiptDetail.deleteCompetenceConfirm", { name: deleteCredit?.name })}
        onConfirm={handleDeleteCredit}
        onCancel={() => setDeleteCredit(null)}
      />
      <CreditEditDialog credit={editCredit} onClose={() => setEditCredit(null)} onSaved={fetchAll} />
      <CreditCreateDialog
        open={addCreditOpen}
        personId={partnerId ?? ""}
        period={period ?? ""}
        partnerType={partner?.partnerType}
        onClose={() => setAddCreditOpen(false)}
        onSaved={fetchAll}
      />
      <ConfirmDialog
        open={waOpen}
        title={t("receipts.wa.confirmTitle")}
        confirmLabel={t("receipts.wa.confirmSend")}
        confirmColor="success"
        message={t("receipts.wa.confirm", { name: partner ? `${partner.firstName} ${partner.lastName}` : "", period })}
        onConfirm={() => {
          if (id && partnerId && period) {
            void sendReceiptWa({
              receiptId: id,
              partnerId,
              fullName: partner ? `${partner.firstName} ${partner.lastName}` : "",
              cells: (partner?.contacts ?? []).map((c) => c.telephoneNumber ?? "").filter(Boolean),
              period,
              settled: Boolean(receipt?.settled)
            });
          }
          setWaOpen(false);
        }}
        onCancel={() => setWaOpen(false)}
      />
    </Box>
  );
}
